import React, { useRef, useState } from 'react';
import { AnimatePresence } from 'motion/react';
import * as motion from 'motion/react-m';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { ButtonColors } from '@osmosis/tailwind-ui/styles/button';
import { IconButton } from '@osmosis/tailwind-ui/components/IconButton';
import { IconButtonProps } from '@osmosis/tailwind-ui/components/IconButton';
import useClickOutside from '../../hooks/useClickOutside';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { useColors } from '@osmosis/tailwind-ui/helpers/useColors';
import { schemes } from '@osmosis/shared/styles';
import { Tooltip } from '@osmosis/tailwind-ui/components/tooltip/Tooltip';
import { bg1 } from '@osmosis/tailwind-ui/styles/background';

type VideoControlButtonProps = IconButtonProps & {
  icon: React.ReactNode;
  ariaLabel: string;
  ariaHaspopup?: boolean;
  ariaControls?: string;
  tooltipText?: string;
  onClick?: () => void;
  popupMenu?: React.ReactNode;
  popupMenuStyle?: React.CSSProperties;
  showPopupOnHover?: boolean;
  'data-testid': string;
  shouldCloseMenu?: boolean;
};

/**
 * VideoControlButton.
 *
 */
export default function VideoControlButton({
  icon,
  ariaLabel,
  ariaHaspopup,
  ariaControls,
  tooltipText,
  onClick,
  popupMenu,
  popupMenuStyle = {},
  showPopupOnHover,
  shouldCloseMenu = false,
  ...props
}: VideoControlButtonProps): JSX.Element {
  const [controlsVisible, setControlsVisible] = useState(false);
  const [isHover, setIsHover] = useState(false);
  const [isFocus, setIsFocus] = useState(false);

  const handleFocus = () => setIsFocus(true);
  const handleBlur = () => setIsFocus(false);
  const handleMouseEnter = () => setIsHover(true);
  const handleMouseLeave = () => setIsHover(false);

  /**
   * Handle click.
   */
  const handleClick = () => {
    setControlsVisible(!controlsVisible);
    onClick?.();
  };

  const colors = useColors();
  const popupMenuRef = useRef<HTMLDivElement>(null);

  useClickOutside(popupMenuRef, () => setControlsVisible(false));

  return (
    <Box className="relative" ref={popupMenuRef}>
      <IconButton
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        data-testid={props['data-testid']}
        key={tooltipText}
        icon={icon}
        onClick={handleClick}
        variant="transparent"
        ariaLabel={ariaLabel}
        aria-haspopup={ariaHaspopup}
        {...(popupMenu || ariaHaspopup
          ? { 'aria-expanded': controlsVisible }
          : {})}
        aria-controls={ariaControls}
        color={colors.text as ButtonColors}
        className={`transition-all duration-300 hover:text-blue-600 [&_svg]:fill-gray-700 hover:[&_svg]:fill-blue-600 max-md:w-9`}
        data-tip
        data-tooltip-id={tooltipText}
        data-tooltip-place="top"
      />

      {!controlsVisible && tooltipText && !showPopupOnHover && (
        /* jscpd:ignore-start */
        <Tooltip
          id={tooltipText}
          style={{
            backgroundColor: schemes.dark['bg-1'],
            color: schemes.dark.text,
          }}
        >
          <P color="inherit" className="center">
            {tooltipText}
          </P>
        </Tooltip>
      )}

      <AnimatePresence>
        {controlsVisible && !showPopupOnHover ? (
          <motion.div
            initial={{ opacity: 0, translateY: 10 }}
            animate={{ opacity: 1, translateY: 0 }}
            exit={{ opacity: 0, translateY: 10 }}
            transition={{ duration: 0.1 }}
            style={{
              position: 'absolute',
              bottom: 45,
              borderRadius: '5px',
              zIndex: 1,
              ...popupMenuStyle,
            }}
          >
            {/* eslint-disable-next-line @nx/workspace/a11y/require-keyboard-handlers */}
            <Box
              onClick={shouldCloseMenu ? handleClick : undefined}
              data-testid="popupMenu"
              className={`r-5 ${bg1} shadow-lg`}
            >
              {popupMenu}
            </Box>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, translateY: 10 }}
            animate={{ opacity: 1, translateY: 0 }}
            exit={{ opacity: 0, translateY: 10 }}
            transition={{ duration: 0.1 }}
            style={{
              position: 'absolute',
              bottom: 45,
              borderRadius: '5px',
              zIndex: 1,
              visibility: isHover || isFocus ? 'visible' : 'hidden',
              ...popupMenuStyle,
            }}
          >
            {showPopupOnHover && (
              /* jscpd:ignore-end */
              <Box
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                data-testid="popupMenu"
                className={`transition-visibility rounded-lg ${bg1} shadow-lg duration-200`}
              >
                {popupMenu}
              </Box>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}
